// Zustand store for dashboard master data management
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { useCallback } from 'react';
import { dashboardMasterService } from '../services/dashboardMasterService';
import {
  DashboardMasterData,
  ComputedDashboardStats,
  CheckpointGridItem,
  LaneGridItem,
  DashboardState,
  CheckpointDataDetail,
  LaneAssignmentDataDetail
} from '../types/dashboardMaster';

interface DashboardMasterStore extends DashboardState {
  // Data getters
  getComputedStats: () => ComputedDashboardStats | null;
  getCheckpointGridData: () => CheckpointGridItem[];
  getLaneGridData: () => LaneGridItem[];
  getCheckpointData: (checkpointId: number) => CheckpointDataDetail | null;
  getLaneAssignmentData: (laneAssignmentId: number) => LaneAssignmentDataDetail | null;
  
  // Actions
  fetchDashboardData: () => Promise<void>;
  checkForUpdates: () => Promise<boolean>;
  forceRefresh: () => Promise<void>;
  startPeriodicUpdates: (intervalMinutes?: number) => () => void;
  stopPeriodicUpdates: () => void;
  clearData: () => void;
  
  // Internal state management
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setMasterData: (data: DashboardMasterData) => void;
}

export const useDashboardMasterStore = create<DashboardMasterStore>()(
  subscribeWithSelector((set, get) => {
    let periodicUpdateStop: (() => void) | null = null;
    let serviceUnsubscribe: (() => void) | null = null;

    // Subscribe to service data changes
    serviceUnsubscribe = dashboardMasterService.subscribe((data: DashboardMasterData) => {
      set({
        masterData: data,
        currentHash: data.hash,
        lastFetch: new Date().toISOString(),
        isLoading: false,
        error: null
      });
    });

    return {
      // Initial state
      masterData: null,
      currentHash: null,
      lastFetch: null,
      isLoading: false,
      error: null,

      // Data getters - call service methods but ensure they're side-effect free
      getComputedStats: () => {
        const { masterData } = get();
        if (!masterData) return null;
        return dashboardMasterService.getComputedStats();
      },

      getCheckpointGridData: () => {
        return dashboardMasterService.getCheckpointGridData();
      },

      getLaneGridData: () => {
        return dashboardMasterService.getLaneGridData();
      },

      getCheckpointData: (checkpointId: number) => {
        return dashboardMasterService.getCheckpointData(checkpointId);
      },

      getLaneAssignmentData: (laneAssignmentId: number) => {
        return dashboardMasterService.getLaneAssignmentData(laneAssignmentId);
      },
      // Actions
      fetchDashboardData: async () => {
        set({ isLoading: true, error: null });
        try {
          const data = await dashboardMasterService.getDashboardMaster();
          set({
            masterData: data,
            currentHash: data.hash,
            lastFetch: new Date().toISOString(),
            isLoading: false,
            error: null
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to fetch dashboard data'
          });
        }
      },

      checkForUpdates: async () => {
        try {
          const hasUpdates = await dashboardMasterService.checkForUpdates();
          return hasUpdates;
        } catch (error) {
          return false;
        }
      },

      forceRefresh: async () => {
        set({ isLoading: true, error: null });
        try {
          const data = await dashboardMasterService.forceRefresh();
          set({
            masterData: data,
            currentHash: data.hash,
            lastFetch: new Date().toISOString(),
            isLoading: false,
            error: null
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to refresh dashboard data'
          });
        }
      },

      startPeriodicUpdates: (intervalMinutes = 2) => {
        // Stop existing updates if any
        if (periodicUpdateStop) {
          periodicUpdateStop();
        }

        periodicUpdateStop = dashboardMasterService.startPeriodicUpdates(intervalMinutes);
        return periodicUpdateStop;
      },

      stopPeriodicUpdates: () => {
        if (periodicUpdateStop) {
          periodicUpdateStop();
          periodicUpdateStop = null;
        }
      },

      clearData: () => {
        dashboardMasterService.clearCache();
        set({
          masterData: null,
          currentHash: null,
          lastFetch: null,
          error: null
        });
      },

      // Internal state management
      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      setError: (error: string | null) => {
        set({ error });
      },

      setMasterData: (data: DashboardMasterData) => {
        set({
          masterData: data,
          currentHash: data.hash,
          lastFetch: new Date().toISOString(),
          isLoading: false,
          error: null
        });
      }
    };
  })
);
// Selector hooks for optimized component updates with stable references
export const useDashboardStats = () => useDashboardMasterStore(
  useCallback((state) => state.getComputedStats(), [])
);
export const useCheckpointGridData = () => useDashboardMasterStore(
  useCallback((state) => state.getCheckpointGridData(), [])
);
export const useLaneGridData = () => useDashboardMasterStore(
  useCallback((state) => state.getLaneGridData(), [])
);
export const useDashboardLoading = () => useDashboardMasterStore(
  useCallback((state) => state.isLoading, [])
);
export const useDashboardError = () => useDashboardMasterStore(
  useCallback((state) => state.error, [])
);
export const useDashboardHash = () => useDashboardMasterStore(
  useCallback((state) => state.currentHash, [])
);
export const useDashboardLastFetch = () => useDashboardMasterStore(
  useCallback((state) => state.lastFetch, [])
);

// Hook to get specific checkpoint data
export const useCheckpointData = (checkpointId: number) => 
  useDashboardMasterStore(state => state.getCheckpointData(checkpointId));

// Hook to get specific lane assignment data  
export const useLaneAssignmentData = (laneAssignmentId: number) =>
  useDashboardMasterStore(state => state.getLaneAssignmentData(laneAssignmentId));

// Hook for dashboard actions
export const useDashboardActions = () => useDashboardMasterStore(state => ({
  fetchDashboardData: state.fetchDashboardData,
  checkForUpdates: state.checkForUpdates,
  forceRefresh: state.forceRefresh,
  startPeriodicUpdates: state.startPeriodicUpdates,
  stopPeriodicUpdates: state.stopPeriodicUpdates,
  clearData: state.clearData
}));